import PageHead from "@/components/PageHead";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { BookOpen, Sparkles, Users, TrendingUp, Download } from "lucide-react";
import AiInsights from "@/components/schools/AiInsights";
import DemoFlow from "@/components/schools/DemoFlow";
import SchoolKpi from "@/components/schools/SchoolKpi";
import SchoolFilters from "@/components/schools/SchoolFilters";
import { useDynamicInsights } from "@/hooks/useDynamicInsights";
import { studioDetails, aiInsightsStudios } from "@/data/greenwoodPersonas";
import { downloadCsv } from "@/lib/csv";
import { cn } from "@/lib/utils";

// Peer-school benchmarks — average completion across comparable studios programmes.
const studioBenchmarks: Record<string, number> = {
  WALS: 82,
  WAFLE: 64,
  WAFFS: 71,
};

export default function StudiosDashboard() {
  const totalActive = studioDetails.reduce((a, s) => a + s.active, 0);
  const avgCompletion = Math.round(
    studioDetails.reduce((a, s) => a + s.completion, 0) / studioDetails.length,
  );
  const avgBenchmark = Math.round(
    studioDetails.reduce((a, s) => a + studioBenchmarks[s.key], 0) / studioDetails.length,
  );
  const totalModules = studioDetails.reduce((a, s) => a + s.modules, 0);

  const exportStudios = () => {
    const rows: (string | number)[][] = [
      ["WeKIT Education Intelligence™ — Learning Studios snapshot"],
      ["Generated", new Date().toISOString()],
      [],
      ["Studio", "Full name", "Active", "Completion %", "Peer benchmark %", "Modules"],
      ...studioDetails.map((s) => [s.name, s.full, s.active, s.completion, studioBenchmarks[s.key], s.modules]),
      [],
      ["Studio", "Module", "Completion %"],
      ...studioDetails.flatMap((s) => s.topModules.map((m) => [s.name, m.title, m.completion])),
    ];
    downloadCsv(`greenwood-studios-snapshot-${new Date().toISOString().slice(0, 10)}.csv`, rows);
  };

  const { insights, contextLabel } = useDynamicInsights(aiInsightsStudios, "studios");

  return (
    <>
      <PageHead title="Learning Studios | WeKIT Education Intelligence™" description="WALS™, WAFLE™ and WAFFS™ learning studios" path="/schools/studios" />
      <div className="container max-w-7xl py-8 space-y-10">
        <header className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 text-xs text-muted-foreground uppercase tracking-wider mb-2">
              <Sparkles className="w-3.5 h-3.5 text-primary" /> Learning Studios
            </div>
            <h1 className="text-3xl md:text-4xl font-semibold tracking-tight">WALS™ · WAFLE™ · WAFFS™</h1>
            <p className="text-muted-foreground mt-1">Cohort progress across every WeKIT studio</p>
          </div>
          <Button size="sm" variant="outline" onClick={exportStudios}>
            <Download className="w-3.5 h-3.5 mr-1" /> Export CSV
          </Button>
        </header>

        <SchoolFilters />

        <section className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <SchoolKpi icon={<Users className="w-4 h-4" />}       label="Active learners"       value={totalActive.toLocaleString()} hint="Across 3 studios" tone="positive" series={[2400, 2560, 2680, 2780, 2860, 2920, 2952]} />
          <SchoolKpi icon={<TrendingUp className="w-4 h-4" />}  label="Average completion"    value={`${avgCompletion}%`}          hint="All studios"      tone="positive" series={[68, 72, 76, 79, 81, 82, 83]} benchmark={avgBenchmark} benchmarkSuffix="%" />
          <SchoolKpi icon={<BookOpen className="w-4 h-4" />}    label="Modules live"          value={totalModules}                 hint="WALS + WAFLE + WAFFS"                 series={[24, 26, 27, 28, 29, 30, 30]} />
          <SchoolKpi                                            label="Top studio"            value="WALS™"                        hint={`${studioDetails[0].completion}% completion`} tone="positive" series={[86, 88, 90, 91, 92, 93, 94]} />
        </section>

        <section className="grid md:grid-cols-3 gap-4">
          {studioDetails.map((s) => {
            const bench = studioBenchmarks[s.key];
            const delta = s.completion - bench;
            const isUp = delta >= 0;
            return (
              <Card key={s.key} className="overflow-hidden border-0 ring-1 ring-border">
                <div className={cn("h-1.5 bg-gradient-to-r", s.color)} />
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base">{s.name}</CardTitle>
                    <Badge variant="secondary" className="rounded-full">{s.modules} modules</Badge>
                  </div>
                  <p className="text-xs text-muted-foreground">{s.full}</p>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <MiniStat icon={<Users className="w-3.5 h-3.5" />} label="Active" value={s.active.toLocaleString()} />
                    <MiniStat icon={<BookOpen className="w-3.5 h-3.5" />} label="Completion" value={`${s.completion}%`} />
                  </div>
                  <Progress value={s.completion} className="h-1.5" />
                  <div
                    className={cn(
                      "inline-flex items-center gap-1 text-[11px] rounded-full px-2 py-0.5 ring-1",
                      isUp
                        ? "bg-emerald-500/10 text-emerald-600 ring-emerald-500/20"
                        : "bg-rose-500/10 text-rose-600 ring-rose-500/20",
                    )}
                    aria-label={`${Math.abs(delta)} percentage points ${isUp ? "above" : "below"} peer benchmark`}
                  >
                    {isUp ? "▲" : "▼"} {Math.abs(delta)}pp vs peers ({bench}%)
                  </div>
                  <div className="space-y-2 pt-1">
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Top modules</div>
                    {s.topModules.map((m) => (
                      <div key={m.title}>
                        <div className="flex items-center justify-between text-sm">
                          <span className="truncate pr-2">{m.title}</span>
                          <span className="tabular-nums text-muted-foreground">{m.completion}%</span>
                        </div>
                        <Progress value={m.completion} className="h-1 mt-1" />
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </section>

        <AiInsights insights={insights} contextLabel={contextLabel} title="Studio insights" subtitle="What's landing and what needs a nudge" />
        <DemoFlow />
      </div>
    </>
  );
}

function MiniStat({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="rounded-lg border p-2.5">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-muted-foreground">
        {icon}<span>{label}</span>
      </div>
      <div className="text-lg font-semibold tabular-nums mt-0.5">{value}</div>
    </div>
  );
}
